import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { OrganizationAlertListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { client } from "@/hono";
import { EditAlertModal } from "@/modals/organizations/edit-alert-modal";

export function RowActions({ row }: CellContext<OrganizationAlertListItem, "actions">) {
	/** Context */
	const { refetch } = useDataTableContext();
	const { t } = useTranslation();

	/** Hooks */
	const asyncModal = useAsyncModal();
	const editAlertModalDisclosure = useDisclosure();

	/** Functions */
	async function handleOnDeleteAlert() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.organizations.alerts[":alertId"].$delete({
			param: { alertId: row.original.id },
		});
		await responseHandler(response);
		await refetch?.();
	}

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper>
				<Menu
					items={createStandardMenuItems({
						onEdit: editAlertModalDisclosure.onOpen,
						onDelete: handleOnDeleteAlert,
					})}
				>
					<Button
						size="xxs"
						variant="ghost"
						icon={<Icon name="EllipsisVertical" />}
					/>
				</Menu>
			</RowActionsWrapper>
			{editAlertModalDisclosure.isOpen && (
				<EditAlertModal
					isOpen={editAlertModalDisclosure.isOpen}
					onClose={editAlertModalDisclosure.onClose}
					alertId={row.original.id}
					onComplete={refetch}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					message={t("dashboard.alerts.delete_confirmation_message")}
					controller={asyncModal}
				/>
			)}
		</Fragment>
	);
}
